import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import ConfirmationModal from "../common/ConfirmationModal";
import { sendEvent } from "../../utils/gtag";

const COMPANY_EMAILS = ["info@techvelly.com", "sales@techvelly.com"];

const budgetOptions = [
  "300$ - 1000$ Esmtimated (20% off for Black Friday)",
  "1000$ - 5000$ Estimated (30% off for Black Friday)" ,
  "5000$ - 10000$ Estimated (40% off for Black Friday)",
  "More than 10000$ Estimated (40% off for Black Friday)",
];

export default function HeroSection({ highlightForm }) {
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [showPopup, setShowPopup] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    project: "",
    budget: "",
    country: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [budgetOpen, setBudgetOpen] = useState(false);
  const budgetRef = useRef(null);

  const animatedTexts = [
    "AI-Driven Software",
    "Web Development",
    "Mobile App Development",
    "FinTech Solutions",
    "EduTech Innovations",
    "HealthTech Applications",
    "E-Commerce Platforms",
    "Realstate Websites",
  ];

  useEffect(() => {
    const textInterval = setInterval(() => {
      setCurrentTextIndex((prevIndex) => (prevIndex + 1) % animatedTexts.length);
    }, 3000);

    const popupTimer = setTimeout(() => {
      setShowPopup(true);
    }, 20000);

    return () => {
      clearInterval(textInterval);
      clearTimeout(popupTimer);
    };
  }, [animatedTexts.length]);

  useEffect(() => {
    if (showPopup) {
      const disappearTimer = setTimeout(() => {
        setShowPopup(false);
      }, 15000);
      return () => clearTimeout(disappearTimer);
    }
  }, [showPopup]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (budgetRef.current && !budgetRef.current.contains(event.target)) {
        setBudgetOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const validateForm = () => {
    const newErrors = {
      name: formData.name ? "" : "This field is required",
      email: formData.email ? "" : "This field is required",
      phone: formData.phone ? "" : "This field is required",
      project: formData.project ? "" : "This field is required",
      budget: formData.budget ? "" : "This field is required",
    };
    setErrors(newErrors);
    return Object.values(newErrors).every((e) => e === "");
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handlePhoneChange = (phone, meta) => {
    setFormData((prev) => ({
      ...prev,
      phone,
      country: meta?.country?.name || "",
    }));
    if (errors.phone) setErrors((prev) => ({ ...prev, phone: "" }));
  };

  const handleBudgetSelect = (budget) => {
    setFormData((prev) => ({ ...prev, budget }));
    setBudgetOpen(false);
    if (errors.budget) setErrors((prev) => ({ ...prev, budget: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);

    try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/quotes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Network response was not ok');
      }

      sendEvent('quotation_request_submit');
      setShowSuccessModal(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        project: "",
        budget: "",
        country: "",
      });
    } catch (error) {
      console.error('There was a problem with your fetch operation:', error);
      setErrorMessage(error.message || "Submission failed. Please try again later.");
      setShowErrorModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="min-h-screen font-sans">
          <style>{`
            @keyframes formHighlight {
              0% { transform: scale(1); box-shadow: 0 0 0px rgba(88, 175, 209, 0); }
              50% { transform: scale(1.02); box-shadow: 0 0 20px rgba(88, 175, 209, 0.7); }
              100% { transform: scale(1); box-shadow: 0 0 0px rgba(88, 175, 209, 0); }
            }
            .react-international-phone-country-selector-button,
            .react-international-phone-input {
                background-color: rgba(255, 255, 255, 0.05) !important;
                border: 1px solid rgba(255, 255, 255, 0.15) !important;
                color: white !important;
                border-radius: 0.5rem;
            }
            .react-international-phone-input {
                border-left: none !important;
                border-top-left-radius: 0 !important;
                border-bottom-left-radius: 0 !important;
            }
            .react-international-phone-country-selector-button {
                padding: 0.375rem 0.75rem !important;
                border-top-right-radius: 0 !important;
                border-bottom-right-radius: 0 !important;
            }
            .react-international-phone-country-selector-dropdown {
                background-color: #bed0e2ff !important;
                border: 1px solid rgba(255, 255, 255, 0.15) !important;
            }
            .react-international-phone-country-selector-dropdown__list-item:hover {
                background-color: rgba(255, 255, 255, 0.1) !important;
            }
            .react-international-phone-country-selector-dropdown__search-input {
                background-color: rgba(255, 255, 255, 0.05) !important;
                border: 1px solid rgba(255, 255, 255, 0.15) !important;
                color: white !important;
            }
        `}</style>
        <section id="hero-section" className="relative min-h-screen bg-gradient-to-tr from-[#264864] via-[#264864] to-[#35a89d] overflow-hidden font-sans">
          {/* Background blobs */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-20 left-10 w-48 h-48 bg-[#35a89d]/5 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-20 right-10 w-64 h-64 bg-[#264864]/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
            <div className="absolute top-1/2 left-1/2 w-56 h-56 bg-white/3 rounded-full blur-2xl animate-pulse delay-2000"></div>
          </div>

          <div className="container mx-auto px-6 pt-20 pb-8 relative z-10">
            <div className="grid lg:grid-cols-[4fr_2fr] gap-16 items-center min-h-[calc(100vh-6rem)]">
              {/* Left Text + Buttons */}
              <div className="text-white space-y-4 lg:space-y-6 pr-0 lg:pr-8">
                <div className="space-y-2">
                  <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black leading-tight tracking-tight whitespace-nowrap">
                    Build •{" "}
                    <span className="bg-gradient-to-r from-[#58afd1] to-white bg-clip-text text-transparent">
                      Innovation
                    </span>{" "}
                    • Success
                  </h1>
                  <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-white/90 leading-tight">
                    Transform Your Business with AI
                  </h2>
                  <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black min-h-[3rem] relative">
                    {animatedTexts.map((text, index) => (
                      <span
                        key={text}
                        className={`absolute transition-all duration-700 ease-in-out ${
                          index === currentTextIndex
                            ? "opacity-100 translate-y-0 scale-100"
                            : "opacity-0 translate-y-6 scale-95"
                        } bg-gradient-to-r from-[#35a89d] to-cyan-300 bg-clip-text text-transparent`}
                      >
                        {text}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  {/* Book a Call Button */}
                  <Link to="/request-demo" onClick={() => sendEvent('request_demo')} className="group relative overflow-hidden bg-gradient-to-br from-[#58afd1] to-[#4a9bc4] text-white font-medium px-6 py-2 text-sm rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-105 hover:shadow-md">
                    <span className="relative z-10 flex items-center gap-2">
                      <svg
                        className="w-4 h-4 transition-transform duration-300 group-hover:-translate-x-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                        />
                      </svg>
                      Book a free Consultancy Call
                    </span>
                    <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-10 rounded-lg transition-all duration-300"></div>
                  </Link>
                  {/* Case Studies Button */}
                  <Link to="/case-studies" onClick={() => sendEvent('view_case_studies_click')} className="group relative overflow-hidden border-2 border-white/60 backdrop-blur-sm text-white font-medium px-6 py-2 text-sm rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-105 hover:shadow-md">
                    <span className="relative z-10 flex items-center gap-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-4 h-4 transition-transform duration-300 group-hover:-translate-x-1"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                      Case Studies
                    </span>
                    <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-10 rounded-lg transition-all duration-300"></div>
                  </Link>

                  
                </div>
              </div>

              {/* Right Form */}
              <div id="quote-form" className={`w-full max-w-md mx-auto lg:mx-0 lg:ml-auto mt-8 lg:mt-0 ${highlightForm ? 'animate-formHighlight' : ''}`}>
                <div className="relative backdrop-blur-xl rounded-2xl border border-white/20 shadow transition-all duration-300 hover:bg-white/10 p-5">
                  <div className="text-center mb-4">
                    <h3 className="text-lg font-semibold text-white mb-1">
                      Get Custom Quote
                    </h3>
                    <p className="text-white/70 text-xs">
                      Tell us about your project and get a tailored solution
                    </p>
                  </div>

                  <form className="space-y-2" onSubmit={handleSubmit}>
                    {/* Name */}
                    <div className="space-y-1">
                      <label className="text-white/80 font-medium text-xs">
                        Name
                      </label>
                      <input
                        type="text"
                        placeholder="Enter your name"
                        value={formData.name}
                        onChange={(e) =>
                          handleInputChange("name", e.target.value)
                        }
                        className="bg-white/5 border border-white/15 text-white placeholder-white/70 focus:border-[#58afd1] focus:ring-[#58afd1] backdrop-blur-sm transition-all duration-300 py-1.5 px-3 rounded-lg w-full text-xs"
                      />
                      {errors.name && (
                        <p className="text-red-400 text-xs mt-0.5">{errors.name}</p>
                      )}
                    </div>

                    {/* Email */}
                    <div className="space-y-1">
                      <label className="text-white/80 font-medium text-xs">
                        Email
                      </label>
                      <input
                        type="email"
                        placeholder="Enter your email"
                        value={formData.email}
                        onChange={(e) =>
                          handleInputChange("email", e.target.value)
                        }
                        className="bg-white/5 border border-white/15 text-white placeholder-white/70 focus:border-[#58afd1] focus:ring-[#58afd1] backdrop-blur-sm transition-all duration-300 py-1.5 px-3 rounded-lg w-full text-xs"
                      />
                      {errors.email && (
                        <p className="text-red-400 text-xs mt-0.5">{errors.email}</p>
                      )}
                    </div>

                    {/* Phone */}
                    <div className="space-y-1">
                      <label className="text-white/80 font-medium text-xs">
                        Country & Phone
                      </label>
                      <PhoneInput
                        defaultCountry="bd"
                        value={formData.phone}
                        onChange={handlePhoneChange}
                        enableSearch={true}
                        placeholder="Enter phone number"
                        inputClassName="w-full"
                      />
                      {errors.phone && (
                        <p className="text-red-400 text-xs mt-0.5">{errors.phone}</p>
                      )}
                    </div>

                    {/* Project */}
                    <div className="space-y-1">
                      <label className="text-white/80 font-medium text-xs">
                        Tell us more about your project
                      </label>
                      <textarea
                        placeholder="Describe your project..."
                        value={formData.project}
                        onChange={(e) =>
                          handleInputChange("project", e.target.value)
                        }
                        className="bg-white/5 border border-white/15 text-white placeholder-white/70 focus:border-[#58afd1] focus:ring-[#58afd1] backdrop-blur-sm transition-all duration-300 min-h-[50px] text-xs px-3 py-1.5 rounded-lg resize-none w-full"
                      />
                      {errors.project && (
                        <p className="text-red-400 text-xs mt-0.5">{errors.project}</p>
                      )}
                    </div>

                    {/* Budget */}
                    <div className="space-y-1 relative" ref={budgetRef}>
                      <label className="text-white/80 font-medium text-xs">
                        Estimated Budget
                      </label>
                      <div
                        onClick={() => setBudgetOpen(!budgetOpen)}
                        className="bg-white/5 border border-white/15 text-white placeholder-white/70 backdrop-blur-sm transition-all duration-300 py-1.5 px-3 rounded-lg w-full flex justify-between items-center text-xs cursor-pointer"
                      >
                        {formData.budget || "Select Budget"}
                        <svg
                          className="w-4 h-4 ml-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                      {budgetOpen && (
                        <div className="absolute mt-1 w-full bg-white rounded-md shadow-lg z-50 text-black text-xs">
                          {budgetOptions.map((b) => (
                            <div
                              key={b}
                              onClick={() => handleBudgetSelect(b)}
                              className="px-2 py-1 hover:bg-gray-200 cursor-pointer text-xs"
                            >
                              {b}
                            </div>
                          ))}
                        </div>
                      )}
                      {errors.budget && (
                        <p className="text-red-400 text-xs mt-0.5">{errors.budget}</p>
                      )}
                    </div>

                    {/* Submit */}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      onClick={() => sendEvent('quotation_request_click')}
                      className="w-full bg-gradient-to-r from-[#58afd1] to-[#4a9bc4] hover:from-[#1e3650] hover:to-[#4a9bc4] text-white font-medium py-2 text-xs transition-all duration-400 hover:scale-102 transform hover:-translate-y-0.5 shadow-lg hover:shadow-xl rounded-lg disabled:opacity-50 disabled:cursor-not-allowed mt-1"
                    >
                      {isSubmitting ? "Submitting..." : "Submit Request"}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Download Profile Popup */}
          <div className={`fixed left-1/2 transform -translate-x-1/2 z-50 transition-all duration-500 h-auto ${showPopup ? 'bottom-0 translate-y-0' : '-bottom-full'}`}>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/download/profile`} onClick={() => sendEvent('download_profile_click', { source: 'landing_page_popup' })} className="relative bg-black/50 backdrop-blur-md text-white font-medium px-6 py-4 rounded-lg shadow-lg group cursor-pointer border border-white/20 w-[60vw] block">
                  <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowPopup(false); }} className="absolute -top-2 -right-2 bg-white text-gray-800 rounded-full h-6 w-6 flex items-center justify-center shadow-md">&times;</button>
                  <div className="flex items-center space-x-2 justify-center">
                      <svg
                      className="w-5 h-5 transition-transform duration-300 group-hover:scale-110"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      >
                      <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                      </svg>
                      <span className="text-base font-medium tracking-widest">Download Company Profile</span>
                  </div>
                  <div className="absolute bottom-0 left-0 w-full h-1 bg-white/30 rounded-bl-lg">
                      <div className={`h-full bg-white ${showPopup ? 'animate-timer-line' : ''}`}></div>
                  </div>
              </a>
          </div>

      </div>
      <ConfirmationModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Success!"
        message="Your quote request has been submitted successfully."
        type="success"
      />
      <ConfirmationModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title="Submission Failed"
        message={errorMessage}
        type="error"
        companyEmails={COMPANY_EMAILS}
      />
    </>
  );
}